import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import GlobalUserSearchField from "../../../../global/forms/GlobalUserSearchField";
import {
  GlobalTextField,
  GlobalSelectField,
  GlobalDateField,
  GlobalTextAreaField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import { useScheduleContext } from "../context/ScheduleContext";
import { searchScheduleStaff } from "../../api/schedulesApi";
import {
  buildScheduleCreateSchema,
  buildScheduleEditSchema,
  buildSchedulePayload,
  getDefaultScheduleFormValues,
} from "../../utils/scheduleValidation";
import { formatStaffDepartmentLabel, mapApiDetailsToFormErrors } from "../../utils/scheduleHelpers";
import "../../styles/scheduleDashboard.css";

const ALERT_ID = "schedule-form-alert";

function dropdownOptions(rows, labelKey) {
  return (rows || []).map((r) => ({
    value: String(r.id),
    label: r[labelKey],
  }));
}

function formLegend(mode) {
  if (mode === "create") return "Add schedule";
  if (mode === "edit") return "Edit schedule";
  return "Schedule details";
}

export default function ScheduleForm({
  mode = "create",
  initialValues,
  assignedToName = "",
  onSubmit,
  onCancel,
  submitting = false,
  serverError = "",
}) {
  const {
    scheduleTypes,
    scheduleStatuses,
    priorityTypes,
    loadLookups,
    loadingLookups,
    lookupError,
  } = useScheduleContext();

  const schema = useMemo(
    () => (mode === "edit" ? buildScheduleEditSchema() : buildScheduleCreateSchema()),
    [mode],
  );

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues || getDefaultScheduleFormValues(),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const applyServerDetails = (details) => {
    const mapped = mapApiDetailsToFormErrors(details);
    Object.entries(mapped).forEach(([field, message]) => {
      setError(field, { type: "server", message });
    });
  };

  const submitHandler = async (values) => {
    try {
      await onSubmit?.(buildSchedulePayload(values));
    } catch (err) {
      if (err?.details) applyServerDetails(err.details);
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} noValidate>
      <GlobalFormShell
        legend={formLegend(mode)}
        alertId={ALERT_ID}
        showAlert={Boolean(serverError || lookupError)}
        alertText={serverError || lookupError}
        gridClassName="gfs-form-grid--compact"
        overflowVisible
        actions={
          <>
            {onCancel ? (
              <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </GlobalButton>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Create schedule"}
            </GlobalButton>
          </>
        }
      >
        <Controller
          name="schedule_title"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="schedule_title"
              label="Schedule Title"
              required
              value={field.value}
              onChange={field.onChange}
              onBlur={(e) => {
                field.onBlur(e);
                const trimmed = String(field.value ?? "").trim();
                if (trimmed !== field.value) field.onChange(trimmed);
              }}
              error={errors.schedule_title?.message}
              placeholder="e.g. Team meeting"
              maxLength={200}
              minLength={3}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="schedule_type_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="schedule_type_id"
              label="Schedule Type"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.schedule_type_id?.message}
              options={dropdownOptions(scheduleTypes, "type_name")}
              placeholderOption="Select type"
              disabled={loadingLookups}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="schedule_date"
          control={control}
          render={({ field }) => (
            <GlobalDateField
              id="schedule_date"
              label="Date"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.schedule_date?.message}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="schedule_time"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="schedule_time"
              label="Time"
              required
              type="time"
              value={field.value}
              onChange={field.onChange}
              error={errors.schedule_time?.message}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="location"
              label="Location"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.location?.message}
              placeholder="e.g. Conference room"
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <GlobalTextAreaField
              id="description"
              label="Description"
              value={field.value}
              onChange={field.onChange}
              error={errors.description?.message}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="assigned_to"
          control={control}
          render={({ field }) => (
            <GlobalUserSearchField
              label="Assigned To"
              required
              value={field.value}
              onChange={(v) => field.onChange(v != null && v !== "" ? String(v) : "")}
              searchUsers={searchScheduleStaff}
              error={errors.assigned_to?.message}
              initialDisplayName={assignedToName || initialValues?.assigned_to_name}
              initialDisplayDepartment={formatStaffDepartmentLabel({})}
              wrapClassName="gus-wrap--form-span"
              placeholder="Type name (min 2 letters)…"
              helperText="Search staff by name, then click a result or press Enter."
            />
          )}
        />
        <Controller
          name="priority_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="priority_id"
              label="Priority"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.priority_id?.message}
              options={dropdownOptions(priorityTypes, "priority_name")}
              placeholderOption="Select priority"
              disabled={loadingLookups}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="status_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="status_id"
              label="Status"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.status_id?.message}
              options={dropdownOptions(scheduleStatuses, "status_name")}
              placeholderOption="Select status"
              disabled={loadingLookups}
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="reminder"
          control={control}
          render={({ field }) => {
            const on = Boolean(field.value);
            return (
              <div
                className={`sch-reminder-card gff-field--full${on ? " sch-reminder-card--on" : ""}`}
              >
                <label className="sch-reminder-card__toggle" htmlFor="schedule_reminder">
                  <input
                    id="schedule_reminder"
                    type="checkbox"
                    checked={on}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  <span className="sch-reminder-card__copy">
                    <span className="sch-reminder-card__title">Reminder</span>
                    <span className="sch-reminder-card__hint">
                      {on
                        ? "On — this schedule is marked for a reminder to the assignee."
                        : "Optional. Turn on to flag this schedule for a reminder."}
                    </span>
                  </span>
                </label>
              </div>
            );
          }}
        />
        <Controller
          name="remarks"
          control={control}
          render={({ field }) => (
            <GlobalTextAreaField
              id="remarks"
              label="Remarks"
              value={field.value}
              onChange={field.onChange}
              error={errors.remarks?.message}
              baseAlertId={ALERT_ID}
              classNames={{ fieldWrap: "gff-field gff-field--full" }}
            />
          )}
        />
      </GlobalFormShell>
    </form>
  );
}
